import { type TypedDocumentNode, useQuery } from '@apollo/client';
import { useMemo } from 'react';
import type { CommodityOptionsQuery, CommodityOptionsQueryVariables } from 'types/graphql';
import { useDebounceValue } from 'usehooks-ts';

/** How many commodities a picker shows before the user types. */
export const COMMODITY_OPTIONS_PAGE_SIZE = 50;
/** Typing pauses this long before the server search runs. */
export const COMMODITY_SEARCH_DEBOUNCE_MS = 300;
/** Options read A→Z, so the first page is the start of the alphabet. */
const BY_NAME = [{ field: 'name', sort: 'asc' }];

/**
 * A commodity picker's bounded read (TAS-3660 NN07): one `commodityPage` page, searched by name.
 * Replaces the production run page's and the Add Product drawer's `commodities(take: 5000)` reads.
 */
export const COMMODITY_OPTIONS_QUERY: TypedDocumentNode<CommodityOptionsQuery, CommodityOptionsQueryVariables> = gql`
    query CommodityOptionsQuery($input: StandardPageInput!) {
      commodityPage(input: $input) {
        edges {
          node {
            id
            name
          }
        }
      }
    }
  `;

export type CommodityOption = NonNullable<
  NonNullable<NonNullable<CommodityOptionsQuery['commodityPage']['edges']>[number]>['node']
>;

const nodesOf = (data: CommodityOptionsQuery | undefined) =>
  (data?.commodityPage.edges ?? []).flatMap((edge) => (edge?.node ? [edge.node] : []));

/**
 * Search-as-you-type commodities: the first page once the picker is used, then a debounced server
 * search, so an organization past the page size reaches every commodity by typing. The previous
 * options stay while a search is in flight.
 */
export function useCommodityOptions({ searchText = '', skip = false }: { searchText?: string; skip?: boolean }): {
  commodities: CommodityOption[];
  loading: boolean;
} {
  const [search] = useDebounceValue(searchText.trim(), COMMODITY_SEARCH_DEBOUNCE_MS);
  const input = useMemo(
    () => ({ first: COMMODITY_OPTIONS_PAGE_SIZE, sortItems: BY_NAME, ...(search ? { searchText: search } : {}) }),
    [search]
  );
  const page = useQuery(COMMODITY_OPTIONS_QUERY, {
    variables: { input },
    skip,
    // Fresh on open (the app default is cache-and-network); a pick returns to the first page without a re-read.
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: () => 'cache-first',
  });
  const commodities = useMemo(() => nodesOf(page.data ?? page.previousData), [page.data, page.previousData]);

  return { commodities, loading: page.loading };
}
